/**
 * Refer — MS530 dedicated /refer surface.
 *
 * AUDIT-019 + AUDIT-020 (MS523 Phase 0): Refer-a-friend was buried as
 * a SectionCard inside /settings, where its `size="lg"` copy-link CTA
 * and `text-base font-mono` share URL fought every neighbouring
 * Settings button (all of which are `size="sm"`). Linear/Notion/Figma
 * all give referrals a dedicated nav surface — it's a Growth action,
 * not an account-config action. Promoting it to its own page also
 * dissolves the typography clash because there are no neighbouring
 * Settings buttons to compete with.
 *
 * The data plumbing (ensure_referral_code RPC + referrals + profiles
 * reads) is identical to the prior ReferAFriendBlock inside Settings.tsx;
 * this is a move + standalone layout, not a redesign of the underlying
 * UX. Any UX changes to the share link / copy CTA itself belong in a
 * follow-up ticket (MS530b).
 */

import { useState } from "react";
import { format } from "date-fns";
import { CheckCircle2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { PageHeader } from "@/components/lembra/PageHeader";
import { PageShell } from "@/components/lembra/PageShell";
import { PageMeta } from "@/components/lembra/PageMeta";
import { Button } from "@/components/ui/button";

function ReferContent() {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const codeQuery = useQuery({
    enabled: !!user?.id,
    queryKey: ["referral", "code", user?.id],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.rpc("ensure_referral_code");
      if (error) throw error;
      return (data as string | null) ?? null;
    },
  });

  const countsQuery = useQuery({
    enabled: !!user?.id,
    queryKey: ["referral", "counts", user?.id],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("referrals")
        .select("status")
        .eq("referrer_user_id", user!.id);
      if (error) throw error;
      const rows = (data as { status: string }[] | null) ?? [];
      return {
        total: rows.length,
        pending: rows.filter((r) => r.status === "pending").length,
        converted: rows.filter(
          (r) => r.status === "converted" || r.status === "rewarded",
        ).length,
      };
    },
  });

  const creditQuery = useQuery({
    enabled: !!user?.id,
    queryKey: ["referral", "credit", user?.id],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("referral_credit_cents, referral_credit_expires_at")
        .eq("id", user!.id)
        .single();
      if (error) throw error;
      return {
        cents: Number(data?.referral_credit_cents ?? 0),
        expiresAt: (data?.referral_credit_expires_at as string | null) ?? null,
      };
    },
  });

  const code = codeQuery.data ?? "";
  const shareUrl = code
    ? `${typeof window !== "undefined" ? window.location.origin : "https://lembra.com.au"}/?ref=${code}`
    : "";
  const counts = countsQuery.data ?? { total: 0, pending: 0, converted: 0 };
  const creditCents = creditQuery.data?.cents ?? 0;
  const creditExpiresAt = creditQuery.data?.expiresAt ?? null;

  const onCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
      toast("Share link copied", {
        description:
          "Paste it into Slack, email, or anywhere you'd like to share.",
      });
    } catch {
      toast("Couldn't copy automatically", {
        description: shareUrl,
      });
    }
  };

  if (codeQuery.isLoading) {
    return (
      <p
        role="status"
        aria-live="polite"
        className="text-sm text-charcoal/55"
      >
        Loading your referral code…
      </p>
    );
  }

  if (codeQuery.isError || !code) {
    return (
      <p className="text-sm text-coral">
        Couldn't load your referral code. Refresh the page to try again.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {/* MS-batch3: "when they convert" was growth jargon the buyer had
          to decode (and the subtitle restated the same benefit a few
          pixels above). Subtitle now carries the "share anywhere" line;
          this sentence carries the benefit in plain words. */}
      <p className="text-base text-charcoal/75 max-w-[60ch]">
        Friends get <strong>$20 off</strong> their first gift. You get{" "}
        <strong>$20 credit</strong> when they place their first paid order,
        applied automatically to your next order.
      </p>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        {/* MS-batch3: was font-mono text-base — heavy and wrapped raggedly
            under break-all. text-sm sits calmer next to the copy button. */}
        <div className="flex-1 rounded-lg border border-charcoal/15 bg-white px-4 py-3 font-mono text-sm text-navy tabular-nums break-all">
          {shareUrl}
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={onCopy}
          aria-label="Copy referral share link"
          className="min-h-[44px] sm:w-auto"
        >
          {copied ? (
            <>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Copied
            </>
          ) : (
            "Copy link"
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {countsQuery.isLoading || creditQuery.isLoading ? (
          <>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="rounded-lg border border-charcoal/10 bg-charcoal/3 p-3 animate-pulse"
              >
                <div className="h-2.5 w-16 rounded bg-charcoal/15 mb-2" />
                <div className="h-7 w-8 rounded bg-charcoal/15 mb-1.5" />
                <div className="h-2 w-28 rounded bg-charcoal/10" />
              </div>
            ))}
          </>
        ) : (
          <>
            <div className="rounded-lg border border-charcoal/10 bg-charcoal/3 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-charcoal/55">
                Pending
              </p>
              <p className="mt-1 text-2xl font-bold text-navy tabular-nums">
                {counts.pending}
              </p>
              <p className="text-[11px] text-charcoal/55">
                signed up, not yet ordered
              </p>
            </div>
            <div className="rounded-lg border border-charcoal/10 bg-charcoal/3 p-3">
              {/* MS-batch3: "Converted" was growth jargon surfaced to the
                  buyer. "Ordered" reads plainly and parallels "Pending". */}
              <p className="text-[11px] font-semibold uppercase tracking-wider text-charcoal/55">
                Ordered
              </p>
              <p className="mt-1 text-2xl font-bold text-navy tabular-nums">
                {counts.converted}
              </p>
              <p className="text-[11px] text-charcoal/55">
                made a paid order
              </p>
            </div>
            <div className="rounded-lg border border-coral/20 bg-coral/5 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-charcoal/55">
                Your credit
              </p>
              <p className="mt-1 text-2xl font-bold text-coral tabular-nums">
                ${(creditCents / 100).toFixed(0)}
              </p>
              <p className="text-[11px] text-charcoal/55">
                {creditCents > 0 && creditExpiresAt
                  ? `expires ${format(new Date(creditExpiresAt), "d MMM yyyy")}`
                  : creditCents > 0
                    ? "auto-applied at next checkout"
                    : "earned when a friend places their first paid order"}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ReferPage() {
  return (
    <PageShell width="narrow">
      <PageMeta
        title="Refer a friend · Lembra"
        description="Share your Lembra referral code. Friends get $20 off; you get $20 credit when they place a paid order."
        path="/refer"
        noindex
      />
      <PageHeader
        title="Refer a friend"
        emphasis="friend"
        subtitle="Share your code anywhere: Slack, email, or in person."
      />
      <ReferContent />
    </PageShell>
  );
}
